import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "store";
import { type Order, type Refill, TransactionMode, TransactionState } from "types";

type TransactionSlice = {
	order: Order | null;
	refill: Refill | null;
	refillDismissed: boolean;
	currentOrderStep: number;
	// Order ids that were superseded by a local refill starting before the door opened.
	// Subscription pushes keep echoing the previous order in orders.last, so setOrder
	// drops payloads matching this id to prevent the superseded order from reappearing.
	// Cleared only by finishOrderState — once an order is superseded, it stays gone
	// until the next full reset.
	discardedOrderId: string | null;
};

const initialState: TransactionSlice = {
	order: null,
	refill: null,
	refillDismissed: false,
	currentOrderStep: 1,
	discardedOrderId: null,
};

export const transactionSlice = createSlice({
	name: "transaction",
	initialState: initialState,
	reducers: {
		setOrder: (state, { payload }: PayloadAction<Order | null>) => {
			if (payload && payload.id === state.discardedOrderId) {
				return;
			}
			state.order = payload;
		},
		setRefill: (state, { payload }: PayloadAction<Refill | null>) => {
			// Reset dismissed flag when a genuinely new refill arrives
			if (payload && state.refill?.id !== payload.id) {
				state.refillDismissed = false;
				// If a different user starts a local refill while an order is still active
				// (and the door hasn't been opened — backend forbids new transactions after
				// that), the order must end on the FE the same way a new order id replaces
				// the previous one. Record the discarded id so later subscription echoes of
				// orders.last don't reinstate it.
				const isLocalActiveRefill =
					payload.state !== TransactionState.Finished && payload.mode !== TransactionMode.Remote;
				if (isLocalActiveRefill && state.order) {
					state.discardedOrderId = state.order.id;
					state.order = null;
				}
			}
			state.refill = payload;
		},
		dismissRefill: (state) => {
			state.refillDismissed = true;
		},
		updateCurrentOrderStep: (state, action: PayloadAction<number>) => {
			state.currentOrderStep = action.payload;
		},
		finishOrderState: (state) => {
			state.order = null;
			state.refill = null;
			state.refillDismissed = false;
			state.currentOrderStep = 1;
			state.discardedOrderId = null;
		},
	},
});

export const { dismissRefill, finishOrderState, setOrder, setRefill, updateCurrentOrderStep } =
	transactionSlice.actions;
export const selectRefillDismissed = (state: RootState) => state.transaction.refillDismissed;
export const selectOrder = (state: RootState) => state.transaction?.order || null;
export const selectRefill = (state: RootState) => state.transaction?.refill || null;
export const selectUserPreferences = (state: RootState) => state.transaction?.order?.user?.preferences || null;
export const selectOrderState = (state: RootState) => state.transaction.order?.state || null;
export const selectHasActiveOrder = (state: RootState) => state.transaction?.order !== null;
export const selectRefillMode = (state: RootState): TransactionMode | null => {
	const refill = state.transaction.refill;
	if (!refill || refill.state === TransactionState.Finished) {
		return null;
	}
	return refill.mode || null;
};
// Local refills (refill card swiped at the fridge) drive the same OrderStep 2/3/5 transitions
// as customer orders. Remote refills have their own modal (RemoteRefillModal) and are excluded.
// Order takes precedence so that an order started during a refill wind-down keeps its flow.
// Finished local refills are kept so useOrderSteps can transition to OrderStep5 (summary) —
// mirroring the order flow. finishOrderState clears the slot when the post-summary timer expires.
export const selectActiveLocalTransaction = (state: RootState): Order | Refill | null => {
	const order = state.transaction?.order;
	if (order) {
		return order;
	}
	const refill = state.transaction?.refill;
	if (!refill || refill.mode === TransactionMode.Remote) {
		return null;
	}
	return refill;
};
export const selectCurrentOrderStep = (state: RootState) => state.transaction.currentOrderStep;
// Sourced from the active local transaction so refill step 3/5 drawers see removed dishes too.
export const selectOrderDishes = createSelector(
	selectActiveLocalTransaction,
	(transaction) => transaction?.inventory?.removed || [],
);
export const selectIsEmptyOrder = createSelector(selectActiveLocalTransaction, (transaction) => {
	if (!transaction) {
		return false;
	}
	const hasEmptyInventory = transaction.inventory?.removed?.length === 0 || !transaction.inventory;
	return hasEmptyInventory && transaction.state === TransactionState.Finished;
});

// TODO: add fetching logic for when there are unknown dish types in the order
export const selectOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectActiveLocalTransaction],
	(fridgeDishes, transaction) => {
		if (fridgeDishes) {
			if (!transaction?.inventory?.removed) {
				return [];
			}
			const orderDishes = transaction.inventory.removed;
			const fullPriceDishes = orderDishes.filter((dish) => dish.price?.default == null);
			const orderDishTypeIds = new Set(fullPriceDishes.map((dish) => dish?.type?.id));
			const orderDishesById = new Map(orderDishes.map((dish) => [dish.type?.id, dish]));
			return fridgeDishes
				?.filter((dishType) => orderDishTypeIds.has(dishType.id))
				.map((dishType) => {
					const orderDish = orderDishesById.get(dishType.id);
					return {
						...dishType,
						...(orderDish?.price && {
							price: { ...dishType.price, ...orderDish.price },
						}),
					};
				});
		}
		return [];
	},
);

export const selectDiscountedOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectActiveLocalTransaction],
	(fridgeDishes, transaction) => {
		if (fridgeDishes) {
			if (!transaction?.inventory?.removed) {
				return [];
			}
			const orderDishes = transaction.inventory.removed;
			const discountedDishes = orderDishes.filter((dish) => dish.price?.default != null);
			const discountedDishTypeIds = new Set(discountedDishes.map((dish) => dish?.type?.id));
			const discountedDishesById = new Map(discountedDishes.map((dish) => [dish.type?.id, dish]));

			return fridgeDishes
				?.filter((dishType) => discountedDishTypeIds.has(dishType.id))
				.map((dishType) => {
					const discountedDishPrice = discountedDishesById.get(dishType.id)?.price;
					return {
						...dishType,
						...(discountedDishPrice && {
							price: { ...discountedDishPrice },
						}),
					};
				});
		}
		return [];
	},
);

export const selectOrderSummary = createSelector(
	selectActiveLocalTransaction,
	selectOrder,
	selectOrderDishTypes,
	selectDiscountedOrderDishTypes,
	(transaction, order, orderDishTypes, discountedDishTypes) => {
		if (!transaction) {
			return null;
		}

		const orderDishes = transaction.inventory?.removed || [];
		const orderDishTypeIds = new Set(orderDishes.filter((dish) => dish.type?.id != null).map((dish) => dish?.type?.id));

		// Count full-priced dishes per dish type
		const fullPricedDishCounts = orderDishes
			.filter((dish) => dish.price?.default == null)
			.reduce(
				(acc, dish) => {
					const dishTypeId = dish.type?.id;
					if (dishTypeId) {
						acc[dishTypeId] = (acc[dishTypeId] || 0) + 1;
					}
					return acc;
				},
				{} as Record<string, number>,
			);

		// Count discounted dishes per dish type
		const discountedDishCounts = orderDishes
			.filter((dish) => dish.price?.default != null)
			.reduce(
				(acc, dish) => {
					const dishTypeId = dish.type?.id;
					if (dishTypeId) {
						acc[dishTypeId] = (acc[dishTypeId] || 0) + 1;
					}
					return acc;
				},
				{} as Record<string, number>,
			);

		return {
			orderDishTypesCount: orderDishTypeIds.size ?? 0,
			orderDishTypes: orderDishTypes || [],
			discountedDishTypes: discountedDishTypes || [],
			// Refills don't carry a price; OrderPriceSummary tolerates undefined.
			orderPrice: order?.price,
			fullPricedDishCounts: fullPricedDishCounts,
			discountedDishCounts: discountedDishCounts,
		};
	},
);

export const selectOrderPaymentMethod = createSelector(selectOrder, (order) => order?.payment?.method?.type || null);

export default transactionSlice.reducer;
